import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

// 3D Card Background Component
const FloatingGeometry = ({ isHovered }) => {
  const meshRef = useRef();
  const geometryRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.x =
        Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
      meshRef.current.rotation.y = state.clock.elapsedTime * 0.2;
      meshRef.current.position.y = Math.sin(state.clock.elapsedTime) * 0.1;
    }
  });

  return (
    <mesh ref={meshRef} position={[0, 0, 0]} scale={isHovered ? 1.1 : 1}>
      <dodecahedronGeometry ref={geometryRef} args={[0.8, 0]} />
      <meshStandardMaterial
        color={isHovered ? "#10b981" : "#059669"}
        wireframe={true}
        transparent
        opacity={0.3}
      />
    </mesh>
  );
};

const CompanyCard = ({
  company,
  index,
  isActive = false,
  onClick,
  isVisible = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef();

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 50,
      rotateX: -10,
      scale: 0.9,
    },
    visible: {
      opacity: 1,
      y: 0,
      rotateX: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: index * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94],
      },
    },
    hover: {
      y: -10,
      scale: 1.02,
      rotateX: 5,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: index * 0.15 + 0.3,
      },
    },
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      whileHover="hover"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group relative cursor-pointer transition-all duration-500 ${
        isActive
          ? "ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-dark-900"
          : ""
      }`}
      style={{ perspective: "1000px" }}
    >
      {/* Main Card Container */}
      <div
        className={`
        relative overflow-hidden rounded-2xl p-8 h-full min-h-[320px]
        bg-white
        dark:bg-dark-800
        border border-gray-200 dark:border-dark-600
        shadow-lg shadow-gray-200/50 dark:shadow-dark-900/50
        transition-all duration-500
        ${isHovered ? "shadow-2xl shadow-primary-500/20" : ""}
        ${
          isActive
            ? "bg-primary-50 dark:bg-primary-900/20"
            : ""
        }
      `}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 dark:opacity-10">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary-500 rounded-full transform translate-x-16 -translate-y-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent-500 rounded-full transform -translate-x-12 translate-y-12" />
        </div>

        {/* Company Logo/Icon */}
        <motion.div variants={contentVariants} className="relative z-10 mb-6">
          <div className="flex items-center justify-between">
            <div
              className={`
              w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-bold
              ${
                isActive
                  ? "bg-primary-500 text-white"
                  : "bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300"
              }
              transition-all duration-300
              ${isHovered ? "transform rotate-12 scale-110" : ""}
            `}
            >
              {company.logo ? (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="w-10 h-10 object-contain"
                />
              ) : (
                company.name.charAt(0).toUpperCase()
              )}
            </div>

            {/* Company Type Badge */}
            <motion.span
              className={`
                px-3 py-1 rounded-full text-xs font-medium
                ${
                  isActive
                    ? "bg-primary-100 text-primary-700 dark:bg-primary-800 dark:text-primary-200"
                    : "bg-gray-100 text-gray-600 dark:bg-dark-600 dark:text-gray-400"
                }
              `}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 + 0.5 }}
            >
              {company.type || "Company"}
            </motion.span>
          </div>
        </motion.div>

        {/* Company Information */}
        <motion.div variants={contentVariants} className="relative z-10">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-300">
            {company.name}
          </h3>

          <p className="text-primary-600 dark:text-primary-400 font-semibold mb-3">
            {company.position}
          </p>

          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
            <span className="flex items-center">
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
              {company.duration}
            </span>
            {company.location && (
              <>
                <span className="mx-2">•</span>
                <span className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {company.location}
                </span>
              </>
            )}
          </div>

          <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3 mb-4">
            {company.description}
          </p>

          {/* Key Achievements */}
          {company.achievements && company.achievements.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Key Achievements:
              </h4>
              <ul className="space-y-1">
                {company.achievements.slice(0, 2).map((achievement, idx) => (
                  <motion.li
                    key={idx}
                    className="text-xs text-gray-600 dark:text-gray-400 flex items-start"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.7 + idx * 0.1 }}
                  >
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                    {achievement}
                  </motion.li>
                ))}
              </ul>
            </div>
          )}
        </motion.div>

        {/* Technologies Used */}
        {company.technologies && company.technologies.length > 0 && (
          <motion.div
            variants={contentVariants}
            className="relative z-10 mt-6 pt-4 border-t border-gray-200 dark:border-dark-600"
          >
            <div className="flex flex-wrap gap-2">
              {company.technologies.slice(0, 4).map((tech, idx) => (
                <motion.span
                  key={tech}
                  className="px-2 py-1 bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300 text-xs rounded-lg"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 + 0.8 + idx * 0.05 }}
                >
                  {tech}
                </motion.span>
              ))}
              {company.technologies.length > 4 && (
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs rounded-lg">
                  +{company.technologies.length - 4} more
                </span>
              )}
            </div>
          </motion.div>
        )}

        {/* Hover Indicator */}
        <motion.div
          className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={{ rotate: 0 }}
          whileHover={{ rotate: 45 }}
        >
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center text-white">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </motion.div>

        {/* Active Indicator */}
        {isActive && (
          <motion.div
            className="absolute top-4 left-4"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div className="w-3 h-3 bg-primary-500 rounded-full animate-pulse"></div>
          </motion.div>
        )}
      </div>

      {/* 3D Background Effect */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        {/* This would be rendered with Three.js Canvas in the parent component */}
      </div>
    </motion.div>
  );
};

export default CompanyCard;
